/** Store → Payments: Stripe Connect onboarding and status. */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Descriptions, Result, Space, Spin, Tag, Typography, message } from 'antd';
import { CheckCircleFilled, ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { fmtDate, listOf, toastErr, useSite } from './common';

const { Text } = Typography;

const Flag = ({ on, label }) => {
    const { t } = useTranslation();
    return on ? <Tag color="green" icon={<CheckCircleFilled />}>{t(label)}</Tag> : <Tag icon={<ClockCircleOutlined />}>{t(label)}</Tag>;
};

const METHOD_NAMES = { card: 'Cards', apple_pay: 'Apple Pay', alipay: 'Alipay', wechat_pay: 'WeChat Pay' };
const METHOD_STATUS = { active: ['green', 'Active'], pending: ['gold', 'Pending'], inactive: ['default', 'Not enabled'], unavailable: ['default', 'Not available'] };

const PaymentMethods = ({ methods }) => {
    const { t } = useTranslation();
    return (
        <Descriptions column={1} size="small" labelStyle={{ width: 160 }} title={t('Payment methods')} style={{ marginTop: 16 }}>
            {methods.map((m) => {
                const [color, label] = METHOD_STATUS[m.status] || METHOD_STATUS.inactive;
                return (
                    <Descriptions.Item key={m.id} label={t(METHOD_NAMES[m.id] || m.id)}>
                        <Space wrap>
                            <Tag color={color}>{t(label)}</Tag>
                            {m.enable_url && <a href={m.enable_url} target="_blank" rel="noopener noreferrer">{t('Enable in Stripe')}</a>}
                        </Space>
                    </Descriptions.Item>
                );
            })}
        </Descriptions>
    );
};

const Payments = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const location = useLocation();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [soon, setSoon] = useState(false);
    const [busy, setBusy] = useState('');

    const load = useCallback(async () => {
        try { setData((await api.get('/payments', null, QUIET)) || {}); } catch (e) { if (e.status === 404) setSoon(true); else { toastErr(e); setData({}); } }
    }, [api]);
    const refresh = useCallback(async (quiet) => {
        setBusy('refresh');
        try { const d = await api.get('/payments/stripe/refresh'); if (d) setData(d); else await load(); if (!quiet) message.success(t('Updated')); } catch (e) { toastErr(e); } finally { setBusy(''); }
    }, [api, load, t]);

    useEffect(() => {
        const qs = new URLSearchParams(location.search);
        if (qs.get('stripe')) {
            refresh(true);
            navigate(location.pathname, { replace: true });
        } else load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api]);

    const connect = async () => {
        setBusy('connect');
        try {
            const d = await api.post('/payments/stripe/connect', { return_path: location.pathname });
            if (d?.url) window.location.href = d.url; else toastErr({ error: t('Stripe did not return a link') });
        } catch (e) { toastErr(e); setBusy(''); }
    };

    if (soon) return <Card><Result status="info" title={t('Online payments are coming soon')} /></Card>;
    if (!data) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;

    // GET payments → StripeConnect::status (flat): connected, account_id, charges/payouts/details flags, mode, currently_due …
    const acc = data.stripe || data;
    const connected = acc?.connected !== undefined ? !!acc.connected : !!acc?.account_id;
    const active = connected && acc.charges_enabled && acc.details_submitted;
    const mode = acc?.mode || data.mode;
    const platformOff = data.platform_enabled === false || data.enabled === false;

    return (
        <Card title={<Space><span style={{ fontWeight: 700, color: '#635bff' }}>stripe</span>{mode === 'test' && <Tag color="orange">{t('Test mode')}</Tag>}</Space>}
            extra={connected && <Button icon={<ReloadOutlined />} loading={busy === 'refresh'} onClick={() => refresh(false)}>{t('Refresh')}</Button>}>
            {platformOff && <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('Online payments are not available right now.')} />}
            {!connected ? (
                <Result icon={<span style={{ fontSize: 48, color: '#635bff', fontWeight: 800 }}>S</span>} title={t('Accept card payments with Stripe')}
                    subTitle={t('Payouts go straight to your own Stripe account.')}
                    extra={<Button type="primary" size="large" loading={busy === 'connect'} disabled={platformOff} onClick={connect}>{t('Connect Stripe')}</Button>} />
            ) : (
                <>
                    {active
                        ? <Alert type="success" showIcon message={t('Your store can accept payments.')} style={{ marginBottom: 16 }} />
                        : <Alert type="warning" showIcon message={t('Finish setting up your Stripe account to accept payments.')} style={{ marginBottom: 16 }}
                            action={<Button type="primary" loading={busy === 'connect'} onClick={connect}>{t('Continue setup')}</Button>} />}
                    {listOf(acc.currently_due).length > 0 && !active && <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('Stripe still needs {{n}} items from you.', { n: listOf(acc.currently_due).length })} />}
                    {acc.last_error && <Alert type="error" showIcon message={acc.last_error} style={{ marginBottom: 16 }} />}
                    <Descriptions column={1} size="small" labelStyle={{ width: 160 }}>
                        {acc.business_name && <Descriptions.Item label={t('Business')}>{acc.business_name}</Descriptions.Item>}
                        <Descriptions.Item label={t('Account')}><Text copyable>{acc.account_id}</Text></Descriptions.Item>
                        {acc.country && <Descriptions.Item label={t('Country')}>{acc.country}{acc.default_currency ? ` · ${acc.default_currency}` : ''}</Descriptions.Item>}
                        <Descriptions.Item label={t('Status')}>
                            <Space wrap>
                                <Flag on={acc.details_submitted} label="Details submitted" />
                                <Flag on={acc.charges_enabled} label="Charges enabled" />
                                <Flag on={acc.payouts_enabled} label="Payouts enabled" />
                            </Space>
                        </Descriptions.Item>
                        {acc.updated_at && <Descriptions.Item label={t('Last checked')}>{fmtDate(acc.updated_at)}</Descriptions.Item>}
                        {acc.dashboard_url && <Descriptions.Item label={t('Dashboard')}><a href={acc.dashboard_url} target="_blank" rel="noopener noreferrer">{t('Open Stripe dashboard')}</a></Descriptions.Item>}
                    </Descriptions>
                    {listOf(acc.payment_methods).length > 0 && <PaymentMethods methods={listOf(acc.payment_methods)} />}
                </>
            )}
        </Card>
    );
};

export default Payments;
